import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, Box, Typography } from '@mui/material';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import TouchAppIcon from '@mui/icons-material/TouchApp';

const TOTAL_SECONDS = 5;

interface RestaurantKitchenReturnCountdownModalProps {
  open: boolean;
  onDismiss: () => void;
  onTimeout: () => void;
}

/**
 * Mutfağa yazdırma tamamlandıktan sonra masalar ekranına otomatik dönüş için geri sayım.
 * Kullanıcı modal veya arka plana tıklarsa geri sayım iptal edilir.
 */
const RestaurantKitchenReturnCountdownModal: React.FC<RestaurantKitchenReturnCountdownModalProps> = ({
  open,
  onDismiss,
  onTimeout,
}) => {
  const [seconds, setSeconds] = useState(TOTAL_SECONDS);

  useEffect(() => {
    if (!open) {
      setSeconds(TOTAL_SECONDS);
      return;
    }

    setSeconds(TOTAL_SECONDS);

    const interval = setInterval(() => {
      setSeconds(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  useEffect(() => {
    if (open && seconds === 0) {
      onTimeout();
    }
  }, [open, seconds, onTimeout]);

  const handleInteraction = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  if (!open) return null;

  const progress = (seconds / TOTAL_SECONDS) * 100;
  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <Dialog
      open={open}
      onClose={handleInteraction}
      onClick={handleInteraction}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          onClick: handleInteraction,
          sx: { bgcolor: 'rgba(0,0,0,0.55)' },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          cursor: 'pointer',
          userSelect: 'none',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 5,
          px: 3,
          bgcolor: 'rgba(30, 40, 50, 0.97)',
          color: '#fff',
        }}
      >
        <TableRestaurantIcon sx={{ fontSize: 40, color: '#4fc3f7', mb: 2 }} />

        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, textAlign: 'center' }}>
          Masalar ekranına dönülüyor
        </Typography>

        <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', opacity: 0.85, px: 1 }}>
          {TOTAL_SECONDS} saniye içinde masa planına yönlendirileceksiniz.
        </Typography>

        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
          <svg width={120} height={120} viewBox="0 0 120 120">
            <circle
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke="rgba(255,255,255,0.12)"
              strokeWidth="6"
            />
            <circle
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke={seconds <= 2 ? '#ef5350' : '#4fc3f7'}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 60 60)"
              style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s ease' }}
            />
          </svg>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h2"
              fontWeight="bold"
              sx={{
                color: seconds <= 2 ? '#ef5350' : '#fff',
                transition: 'color 0.3s ease',
              }}
            >
              {seconds}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.75 }}>
          <TouchAppIcon sx={{ fontSize: 20 }} />
          <Typography variant="body2" textAlign="center">
            Bu ekranda kalmak için dokunun veya tıklayın
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default RestaurantKitchenReturnCountdownModal;
